import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Send, X } from "lucide-react";
import { Button, Input, cn } from "@eduviaos/ui-kit";
import { ASK_PROMPTS } from "../lib/mockData";
import type { LearnerPortalRole } from "../lib/institution";
import { useMdViewport } from "../lib/useMdViewport";
import { Ziggy } from "./Ziggy";
import { ZIGGY_TAGLINE, type ZiggyAge } from "../lib/ziggy";
import { usePortalLocale } from "../lib/i18n/usePortalLocale";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

interface Bubble {
  id: number;
  from: "user" | "assistant";
  text: string;
}

/**
 * "Ask Ziggy" AI-assist bottom sheet. Prototype: starter prompts fill the
 * composer and a friendly canned reply demonstrates the surface. The real
 * streaming assistant (Ziggy) lands with the F-series AI backend wiring.
 */
export function AskSheet({
  open,
  onClose,
  role,
  ziggyAge = "teen",
}: {
  open: boolean;
  onClose: () => void;
  role: LearnerPortalRole;
  ziggyAge?: ZiggyAge;
}) {
  const reduce = useReducedMotion();
  const isDesktop = useMdViewport();
  const { strings: s } = usePortalLocale();
  const [draft, setDraft] = React.useState("");
  const [thread, setThread] = React.useState<Bubble[]>([]);
  const nextId = React.useRef(1);
  const prompts = ASK_PROMPTS[role];
  const composerRef = React.useRef<HTMLInputElement>(null);
  const threadEndRef = React.useRef<HTMLDivElement>(null);

  // Escape closes the sheet — a dialog the keyboard can't dismiss is a trap.
  React.useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Move focus into the sheet so screen readers and keyboards land inside it.
  React.useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => composerRef.current?.focus(), 120);
    return () => window.clearTimeout(timer);
  }, [open]);

  React.useEffect(() => {
    if (thread.length === 0) return;
    threadEndRef.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "end" });
  }, [thread, reduce]);

  function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    const userBubble: Bubble = { id: nextId.current++, from: "user", text: trimmed };
    const reply: Bubble = {
      id: nextId.current++,
      from: "assistant",
      text: s.askZiggyCannedReply,
    };
    setThread((t) => [...t, userBubble, reply]);
    setDraft("");
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            aria-hidden
          />
          <motion.div
            role="dialog"
            aria-label={s.askZiggy}
            aria-modal="true"
            initial={
              reduce ? { opacity: 0 } : isDesktop ? { opacity: 0, scale: 0.95 } : { y: "100%" }
            }
            animate={reduce ? { opacity: 1 } : isDesktop ? { opacity: 1, scale: 1 } : { y: 0 }}
            exit={reduce ? { opacity: 0 } : isDesktop ? { opacity: 0, scale: 0.95 } : { y: "100%" }}
            transition={{ duration: isDesktop ? 0.22 : 0.28, ease: EASE_OUT }}
            className={cn(
              "border-border/60 bg-card fixed z-50 flex flex-col border shadow-[var(--shadow-float)]",
              isDesktop
                ? "left-1/2 top-1/2 max-h-[min(80vh,640px)] w-[min(calc(100vw-2rem),28rem)] -translate-x-1/2 -translate-y-1/2 rounded-[1.75rem] lg:w-[min(calc(100vw-4rem),32rem)]"
                : "inset-x-0 bottom-0 mx-auto max-h-[80dvh] max-w-md rounded-t-[1.75rem]",
            )}
          >
            <div className="border-border/60 flex items-center justify-between border-b px-5 py-4">
              <div className="flex items-center gap-2.5">
                <span className="hero-celebrate grid size-11 shrink-0 place-items-end justify-items-center overflow-hidden rounded-2xl pt-1">
                  <Ziggy
                    pose="cheerful"
                    age={ziggyAge}
                    size={44}
                    glow={false}
                    float={false}
                    alt=""
                  />
                </span>
                <div className="leading-tight">
                  <p className="font-display font-semibold">{s.askZiggy}</p>
                  <p className="text-muted-foreground text-xs">{ZIGGY_TAGLINE}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                aria-label={s.close}
                className="text-muted-foreground rounded-full"
              >
                <X className="size-5" />
              </Button>
            </div>

            <div
              className="flex-1 space-y-3 overflow-y-auto px-5 py-4"
              aria-live="polite"
              aria-label={s.askZiggyConversationAria}
            >
              {thread.length === 0 ? (
                <>
                  <div className="flex flex-col items-center gap-2 py-1 text-center">
                    <Ziggy pose="curious" age={ziggyAge} size={132} priority />
                    <p className="text-sm font-medium">{s.askZiggyGreeting}</p>
                  </div>
                  <p className="text-muted-foreground text-sm">{s.askZiggyPromptLead}</p>
                  <div className="flex flex-wrap gap-2">
                    {prompts.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => send(p)}
                        className="border-border/70 bg-muted/50 hover:border-primary/40 hover:bg-primary/8 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors active:scale-[0.98]"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                thread.map((b) => (
                  <div
                    key={b.id}
                    className={cn("flex", b.from === "user" ? "justify-end" : "justify-start")}
                  >
                    <div
                      className={cn(
                        "max-w-[80%] rounded-2xl px-3.5 py-2 text-sm",
                        b.from === "user"
                          ? "bg-primary text-primary-foreground rounded-br-sm"
                          : "bg-muted text-foreground rounded-bl-sm",
                      )}
                    >
                      {b.text}
                    </div>
                  </div>
                ))
              )}
              <div ref={threadEndRef} />
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send(draft);
              }}
              className="border-border/60 flex items-center gap-2 border-t px-4 py-3"
            >
              <Input
                ref={composerRef}
                id="ask-composer"
                name="ask"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={s.askZiggyPlaceholder}
                className="h-11 flex-1 rounded-full"
                aria-label={s.askZiggyComposerAria}
              />
              <Button
                type="submit"
                size="icon"
                disabled={!draft.trim()}
                aria-label={s.askZiggySend}
                className="size-11 shrink-0 rounded-full transition-transform duration-150 active:scale-95"
              >
                <Send className="size-5" />
              </Button>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
